/**
 * Access-list target KINDS — the one place the web tree knows that a target row can
 * be a MongoDB Atlas project or a DigitalOcean managed-database firewall (052).
 *
 * Pure and dependency-free on purpose: `scripts/check-cluster-access.mjs` §4 imports
 * this file directly under plain node and calls the helpers, so a form gate or an
 * input shape that drifts is a failing test rather than a 400 from `createAtlasProject`
 * on qa. The numbers (cap 100, warn 90, crit 97) are NOT here — `capacityFor(kind)`
 * in `@infras/shared-types` owns them, same rule as `ATLAS_CAP` (see `atlas.ts`).
 *
 * Wire value of `AtlasProject.kind` / `AtlasProjectOverview.kind` (contract §5) — the
 * shared-types union, re-exported type-only so plain node can still import this file.
 */
import type { TargetKind } from '@infras/shared-types';
export type { TargetKind };

/** Anything the wire could send that is not `do_db` is an Atlas project — the
 *  column defaults to `atlas` and every pre-052 row carries it. */
export function asTargetKind(v: string | null | undefined): TargetKind {
  return v === 'do_db' ? 'do_db' : 'atlas';
}

/** Badge text on a row / drawer. Short, because it sits beside the name. */
export const KIND_LABEL: Record<TargetKind, string> = {
  atlas: 'Atlas',
  do_db: 'DO firewall',
};

/** The kind picker's words — what a person chooses, not the wire token. */
export const KIND_PICK_LABEL: Record<TargetKind, string> = {
  atlas: 'MongoDB Atlas',
  do_db: 'DigitalOcean',
};

/** `<name> (DO)` in the mapping dropdowns (AC-27); Atlas entries unchanged. */
export function targetOptionLabel(p: {
  projectKey: string;
  displayName?: string | null;
  kind?: string | null;
}): string {
  const name = p.displayName || p.projectKey;
  return asTargetKind(p.kind) === 'do_db' ? `${name} (DO)` : name;
}

/** Dropdown row for a scanned DO database: `name · engine · team` (AC-18). */
export function doDatabaseLabel(d: { name: string; engine?: string | null; doTeam: string }): string {
  return `${d.name} · ${d.engine || 'unknown engine'} · ${d.doTeam}`;
}

/**
 * Shown INSTEAD of an empty select when nothing has been scanned yet (AC-19). Names
 * the CLI and the job because the dropdown is fed by the scan, which is seeded off and
 * needs `DO_TEAM_TOKENS_JSON` — the bootstrap runbook runs it once before this form.
 * There is deliberately no free-text uuid field to fall back to.
 */
export const DO_SCAN_HINT =
  'No DigitalOcean database has been scanned yet. Run `pnpm --filter @infras/api scan do-db-firewall` (or enable the do-db-firewall-scan job) with DO_TEAM_TOKENS_JSON set, then reopen this form — the database list comes from that scan.';

/**
 * Whether a target's kind offers the prune controls at all.
 *
 * `true` for BOTH kinds since the owner decision of 2026-09-04 (timeline e-32): a DO
 * firewall address is revoked exactly the way an Atlas one is — root-gated preview →
 * confirm → fresh GET → PUT without the confirmed addresses → GET verify, and the
 * reported row is suppressed so Sync now cannot re-add it. The v1 refusal this helper
 * encoded (`TargetKindError` on every do_db delete) is gone with that decision.
 *
 * Kept as a function rather than deleted: the kind seam is where a future
 * delete-less target kind would say so, and the drawer, `check-cluster-access.mjs` §4
 * and the AC read one rule instead of three inline `!== 'do_db'` tests. Role is the
 * OTHER half of the gate and lives in `canPruneAtlas` — both must pass.
 */
export const canPruneKind = (kind: TargetKind): boolean => kind === 'atlas' || kind === 'do_db';

/**
 * `Sync now` on a do_db drawer (e-11) is the existing `reconcileAtlasWhitelist`
 * mutation, `@RequireRole` root|maintainer. Mirrors the API gate so the button does
 * not exist only to return 403; the API is the enforcement.
 */
export const canSyncNow = (role: string | null | undefined): boolean =>
  role === 'root' || role === 'maintainer';

// ── Add-target form ──────────────────────────────────────────────────────────

export interface AddTargetForm {
  kind: TargetKind;
  projectKey: string;
  /** atlas: the Atlas project id · do_db: the DO database uuid, from the dropdown. */
  projectId: string;
  clientId: string;
  /** atlas: OAuth client secret · do_db: the DO API token. Encrypted at rest either way. */
  clientSecret: string;
  /** do_db only — derived from the chosen database, never typed. */
  doTeam: string;
  displayName: string;
  description: string;
}

export const EMPTY_TARGET_FORM: AddTargetForm = {
  kind: 'atlas',
  projectKey: '',
  projectId: '',
  clientId: '',
  clientSecret: '',
  doTeam: '',
  displayName: '',
  description: '',
};

/** Create enables on key + database + token for do_db; on key + id + client id +
 *  secret for atlas — the second is exactly today's gate (AC-18). */
export function addTargetReady(f: AddTargetForm): boolean {
  const base = Boolean(f.projectKey.trim() && f.projectId.trim() && f.clientSecret.trim());
  if (f.kind === 'do_db') return base && Boolean(f.doTeam.trim());
  return base && Boolean(f.clientId.trim());
}

/**
 * The `CreateAtlasProjectInput` for each kind (contract §5). Atlas sends what it sent
 * before this change — no `kind` key, so an api that predates 052 still accepts it.
 * do_db sends `clientId: null` (the server rejects a non-empty one) and the team the
 * dropdown derived.
 */
export function createTargetInput(f: AddTargetForm): Record<string, unknown> {
  const common = {
    projectKey: f.projectKey.trim(),
    projectId: f.projectId.trim(),
    // F-5 (review e-11): trimmed. A pasted DO token or OAuth secret carries a
    // trailing newline often enough — from a terminal, a password manager or a
    // wrapped file — and the untrimmed value is stored encrypted, so the failure
    // surfaces later as a 401 from the vendor with nothing on screen to explain it.
    clientSecret: f.clientSecret.trim(),
    displayName: f.displayName.trim() || null,
    description: f.description.trim() || null,
  };
  if (f.kind === 'do_db') {
    return { ...common, kind: 'do_db', doTeam: f.doTeam.trim(), clientId: null };
  }
  return { ...common, clientId: f.clientId.trim() };
}

// ── Target key: what it may be, and what a name derives into ─────────────────

/**
 * The shape of a `projectKey`. Lowercase, digits and single hyphens, 2–64 chars,
 * starting on an alphanumeric — the form every existing row already has
 * (`prod-atlas`, `e2e-do-db-devqa`) and the only shape agents, URLs and the
 * `/cluster-access/project/<key>` path can carry without escaping.
 *
 * The API does NOT validate this (`CreateAtlasProjectInput.projectKey` is a bare
 * `@Field()`), which is exactly why the rule is written down on the side that can
 * still tell the operator before the row exists: a key is a permanent identifier —
 * `updateAtlasProject` has no `projectKey` field, so a typo can never be renamed.
 */
export const TARGET_KEY_RE = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;

export function isValidTargetKey(key: string): boolean {
  return TARGET_KEY_RE.test(key) && !key.includes('--');
}

/**
 * The key the wizard proposes from the display name — so the operator types one
 * name, not a name and a slug that must agree with it.
 *
 * A do_db key is prefixed with the DigitalOcean TEAM the picked database belongs
 * to, because a database name is only unique inside its team: `optisigns-internal`
 * exists on more than one, and two targets cannot share a key. The prefix is
 * skipped when the name already starts with it, so picking a database that is
 * already named after its team does not produce `optisigns-lite-optisigns-lite-…`.
 *
 * Proposal only. The field stays editable and stops being re-derived the moment it
 * is touched — see `AddTargetWizard`.
 */
export function deriveTargetKey(name: string, kind: TargetKind, team = ''): string {
  const slug = (s: string) =>
    s
      .normalize('NFKD')
      // Strip combining marks so `Übung` reads `ubung`, not `u-bung`.
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  const base = slug(name);
  if (!base) return '';
  const prefix = kind === 'do_db' ? slug(team) : '';
  const full = !prefix || base === prefix || base.startsWith(`${prefix}-`) ? base : `${prefix}-${base}`;
  // Cap, then re-trim: slicing mid-word can leave a trailing hyphen, which is not a key.
  return full.slice(0, 64).replace(/-+$/, '');
}
